import { FormInput } from '@/shared/components'
import { ApplicationAddressFields } from './ApplicationAddressFields'
import { ENROLLMENT_FIELD_LABELS } from '../../../constants/enrollmentContent'
import type { EnrollmentFieldEditability } from '../../../utils/enrollmentHydration'
import type {
  AddressParts,
  ApplicationFormData,
  FieldErrorKey,
  FieldErrors,
  UpdateApplicationField
} from '../types'

type ApplicationFormDetailsSectionProps = {
  addressStateRestriction: string
  fieldErrors: FieldErrors
  fieldEditability: EnrollmentFieldEditability
  formData: ApplicationFormData
  fullAddress: string
  onAddressChange: (value: string) => void
  onAddressSelect: (parts: AddressParts) => void
  onClearFieldError: (field: FieldErrorKey) => void
  onEmailChange: (value: string) => void
  onFieldChange: UpdateApplicationField
  onPhoneNumberChange: (value: string) => void
}

export function ApplicationFormDetailsSection({
  addressStateRestriction,
  fieldErrors,
  fieldEditability,
  formData,
  fullAddress,
  onAddressChange,
  onAddressSelect,
  onClearFieldError,
  onEmailChange,
  onFieldChange,
  onPhoneNumberChange
}: ApplicationFormDetailsSectionProps) {
  return (
    <section className="pb-8 animate-fade-in-up" style={{ animationDelay: '60ms' }}>
      <p className="modal-heading__subtitle mb-4 text-left">
        <span className="modal-heading__subtitle">*</span> Required fields
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <FormInput
            label={
              <span>
                {ENROLLMENT_FIELD_LABELS.firstName} <span className="text-red-500">*</span>
              </span>
            }
            placeholder={ENROLLMENT_FIELD_LABELS.firstName}
            delay={80}
            value={formData.firstName}
            disabled={fieldEditability.firstName === false}
            error={!!fieldErrors.firstName}
            onChange={value => {
              onFieldChange('firstName', value)
              onClearFieldError('firstName')
            }}
          />
          {fieldErrors.firstName ? (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.firstName}</p>
          ) : null}
        </div>
        <div>
          <FormInput
            label={
              <span>
                {ENROLLMENT_FIELD_LABELS.lastName} <span className="text-red-500">*</span>
              </span>
            }
            placeholder={ENROLLMENT_FIELD_LABELS.lastName}
            delay={160}
            value={formData.lastName}
            disabled={fieldEditability.lastName === false}
            error={!!fieldErrors.lastName}
            onChange={value => {
              onFieldChange('lastName', value)
              onClearFieldError('lastName')
            }}
          />
          {fieldErrors.lastName ? (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.lastName}</p>
          ) : null}
        </div>
        <FormInput
          label="Middle Name"
          placeholder="Middle Name (optional)"
          delay={240}
          value={formData.middleName}
          disabled={fieldEditability.middleName === false}
          onChange={value => onFieldChange('middleName', value)}
        />
        <div>
          <FormInput
            label={
              <span>
                {ENROLLMENT_FIELD_LABELS.phoneNumber} <span className="text-red-500">*</span>
              </span>
            }
            placeholder={ENROLLMENT_FIELD_LABELS.phoneNumber}
            type="tel"
            delay={240}
            value={formData.phoneNumber}
            disabled={fieldEditability.phoneNumber === false}
            error={!!fieldErrors.phoneNumber}
            onChange={value => {
              onPhoneNumberChange(value)
              onClearFieldError('phoneNumber')
            }}
          />
          {fieldErrors.phoneNumber ? (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.phoneNumber}</p>
          ) : null}
        </div>
        <div className="md:col-span-2">
          <FormInput
            label={
              <span>
                {ENROLLMENT_FIELD_LABELS.emailAddress} <span className="text-red-500">*</span>
              </span>
            }
            placeholder={ENROLLMENT_FIELD_LABELS.emailAddress}
            type="email"
            delay={320}
            value={formData.email}
            disabled={fieldEditability.email === false}
            error={!!fieldErrors.email}
            onChange={onEmailChange}
          />
          {fieldErrors.email ? (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>
          ) : null}
        </div>
        <ApplicationAddressFields
          addressStateRestriction={addressStateRestriction}
          fieldErrors={fieldErrors}
          fieldEditability={fieldEditability}
          formData={formData}
          fullAddress={fullAddress}
          onAddressChange={onAddressChange}
          onAddressSelect={onAddressSelect}
          onClearFieldError={onClearFieldError}
          onFieldChange={onFieldChange}
        />
      </div>
    </section>
  )
}
